import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Garment, GarmentFilters, PaginatedResponse } from '@/types';
import { garmentService } from '@/services/garmentService';
import { saveGarmentImages, getGarmentImages, deleteGarmentImages } from '@/utils/garmentStorage';
import toast from 'react-hot-toast';

interface WardrobeState {
  garments: Garment[];
  filteredGarments: Garment[];
  filters: GarmentFilters;
  isLoading: boolean;
  hasMore: boolean;
  page: number;
  total: number;
  viewMode: 'grid' | 'list';
  selectedGarment: Garment | null;

  fetchGarments: (reset?: boolean) => Promise<void>;
  addGarment: (garment: Garment) => void;
  removeGarment: (id: string) => Promise<void>;
  updateGarment: (id: string, updates: Partial<Garment>) => Promise<void>;
  setFilters: (filters: Partial<GarmentFilters>) => void;
  resetFilters: () => void;
  setViewMode: (mode: 'grid' | 'list') => void;
  setSelectedGarment: (garment: Garment | null) => void;
  searchSimilar: (garmentId: string) => Promise<Garment[]>;
  toggleFavorite: (id: string) => Promise<void>;
  mergeGarments: (primaryId: string, mergedImageUrls: string[], removeIds: string[]) => void;
  applyFilters: () => void;
}

const defaultFilters: GarmentFilters = {
  sortBy: 'recent',
  sortOrder: 'desc',
};

export const useWardrobeStore = create<WardrobeState>()(
  persist(
    (set, get) => ({
  garments: [],
  filteredGarments: [],
  filters: { ...defaultFilters },
  isLoading: false,
  hasMore: true,
  page: 1,
  total: 0,
  viewMode: 'grid',
  selectedGarment: null,

  fetchGarments: async (reset = false) => {
    const { filters, page, garments } = get();
    const currentPage = reset ? 1 : page;

    set({ isLoading: true });
    try {
      const response: PaginatedResponse<Garment> = await garmentService.list({
        ...filters,
        page: currentPage,
        pageSize: 20,
      });

      // Merge API results with locally-saved garments (which have IDs starting with 'garment-')
      const localGarments = garments.filter(g => g.id.startsWith('garment-'));
      const apiGarments = reset ? response.items : [...garments.filter(g => !g.id.startsWith('garment-')), ...response.items];
      // Deduplicate by ID
      const allIds = new Set<string>();
      const merged = [...localGarments, ...apiGarments].filter(g => {
        if (allIds.has(g.id)) return false;
        allIds.add(g.id);
        return true;
      });

      set({
        garments: merged,
        filteredGarments: merged,
        hasMore: response.hasMore,
        page: currentPage + 1,
        total: merged.length,
        isLoading: false,
      });
    } catch {
      // On API failure, keep existing local garments
      set({ isLoading: false });
      if (garments.length === 0) {
        toast.error('Failed to load garments');
      }
    }
  },

  addGarment: (garment) => {
    // Store images in IndexedDB (async, fire-and-forget) to avoid localStorage quota
    if (garment.imageUrls?.length) {
      saveGarmentImages(garment.id, garment.imageUrls).catch(console.error);
    }
    // Store garment metadata in zustand/localStorage WITHOUT heavy data URLs
    const liteGarment = {
      ...garment,
      imageUrls: garment.imageUrls?.map((_url, i) => `idb://${garment.id}/${i}`),
      thumbnailUrl: garment.imageUrls?.[0] ? `idb://${garment.id}/0` : garment.thumbnailUrl,
    };
    set((state) => ({
      garments: [liteGarment, ...state.garments],
      filteredGarments: [liteGarment, ...state.filteredGarments],
      total: state.total + 1,
    }));
  },

  removeGarment: async (id) => {
    try {
      if (!id.startsWith('garment-')) {
        await garmentService.delete(id);
      }
      // Clean up IndexedDB images
      deleteGarmentImages(id).catch(console.error);
      set((state) => ({
        garments: state.garments.filter((g) => g.id !== id),
        filteredGarments: state.filteredGarments.filter((g) => g.id !== id),
        total: state.total - 1,
      }));
      toast.success('Garment removed');
    } catch {
      toast.error('Failed to remove garment');
    }
  },

  updateGarment: async (id, updates) => {
    try {
      let updated: Garment;
      if (id.startsWith('garment-')) {
        // Local garment - update in-memory directly
        const existing = get().garments.find(g => g.id === id);
        if (!existing) throw new Error('Garment not found');
        updated = { ...existing, ...updates } as Garment;
        // If imageUrls changed, update IndexedDB
        if (updates.imageUrls) {
          const realUrls = updates.imageUrls.filter((u: string) => !u.startsWith('idb://'));
          if (realUrls.length > 0) {
            await saveGarmentImages(id, updates.imageUrls as string[]);
            updated.imageUrls = updates.imageUrls.map((_: string, i: number) => `idb://${id}/${i}`);
            updated.thumbnailUrl = `idb://${id}/0`;
          }
        }
      } else {
        updated = await garmentService.update(id, updates);
      }
      set((state) => ({
        garments: state.garments.map((g) => (g.id === id ? updated : g)),
        filteredGarments: state.filteredGarments.map((g) =>
          g.id === id ? updated : g,
        ),
        selectedGarment:
          state.selectedGarment?.id === id ? updated : state.selectedGarment,
      }));
      toast.success('Garment updated');
    } catch {
      toast.error('Failed to update garment');
    }
  },

  setFilters: (newFilters) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    }));
    get().applyFilters();
  },

  resetFilters: () => {
    set({ filters: { ...defaultFilters } });
    get().fetchGarments(true);
  },

  setViewMode: (viewMode) => set({ viewMode }),

  setSelectedGarment: (selectedGarment) => set({ selectedGarment }),

  searchSimilar: async (garmentId) => {
    try {
      return await garmentService.searchSimilar(garmentId);
    } catch {
      toast.error('Failed to find similar items');
      return [];
    }
  },

  toggleFavorite: async (id) => {
    const garment = get().garments.find((g) => g.id === id);
    if (!garment) return;

    const updated = { isFavorite: !garment.isFavorite };
    set((state) => ({
      garments: state.garments.map((g) =>
        g.id === id ? { ...g, ...updated } : g,
      ),
      filteredGarments: state.filteredGarments.map((g) =>
        g.id === id ? { ...g, ...updated } : g,
      ),
    }));

    try {
      await garmentService.update(id, updated);
    } catch {
      set((state) => ({
        garments: state.garments.map((g) =>
          g.id === id ? { ...g, isFavorite: garment.isFavorite } : g,
        ),
        filteredGarments: state.filteredGarments.map((g) =>
          g.id === id ? { ...g, isFavorite: garment.isFavorite } : g,
        ),
      }));
    }
  },

  mergeGarments: (primaryId, mergedImageUrls, removeIds) => {
    set((state) => {
      const removeSet = new Set(removeIds);
      return {
        garments: state.garments
          .map(g => g.id === primaryId ? { ...g, imageUrls: mergedImageUrls, thumbnailUrl: mergedImageUrls[0] } : g)
          .filter(g => !removeSet.has(g.id)),
        filteredGarments: state.filteredGarments
          .map(g => g.id === primaryId ? { ...g, imageUrls: mergedImageUrls, thumbnailUrl: mergedImageUrls[0] } : g)
          .filter(g => !removeSet.has(g.id)),
        total: state.total - removeIds.length,
      };
    });
    toast.success('Garments merged');
  },

  applyFilters: () => {
    get().fetchGarments(true);
  },
}),
    {
      name: 'vestir-wardrobe',
      partialize: (state) => ({
        garments: state.garments,
        filteredGarments: state.filteredGarments,
        total: state.total,
        viewMode: state.viewMode,
      }),
    },
  ),
);
